import React, { useState, useEffect } from 'react';
import { Shield, ShieldCheck, ShieldX, Smartphone, Copy, Check, Trash2, RefreshCw, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const TwoFactorSettings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [devices, setDevices] = useState([]);
  const [showDevices, setShowDevices] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      setStatus(res.data);
    } catch { toast.error('Failed to load 2FA status'); }
    finally { setLoading(false); }
  };

  const fetchDevices = async () => {
    try {
      const res = await api.get('/auth/2fa/trusted-devices');
      setDevices(res.data.devices || []);
      setShowDevices(true);
    } catch { toast.error('Failed to load devices'); }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleSetup = async () => {
    try {
      const res = await api.post('/auth/2fa/setup');
      setSetupData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Setup failed'); }
  };

  const handleVerifySetup = async () => {
    if (verifyCode.length !== 6) { toast.error('Enter 6-digit code'); return; }
    setSaving(true);
    try {
      await api.post('/auth/2fa/verify-setup', { code: verifyCode });
      toast.success('2FA enabled successfully!');
      setSetupData(null);
      setVerifyCode('');
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); }
    finally { setSaving(false); }
  };

  const handleDisable = async () => {
    if (!disableCode || !disablePassword) { toast.error('Enter code and password'); return; }
    setSaving(true);
    try {
      await api.post('/auth/2fa/disable', { code: disableCode, password: disablePassword });
      toast.success('2FA disabled');
      setShowDisable(false);
      setDisableCode('');
      setDisablePassword('');
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleRevokeDevice = async (id) => {
    try {
      await api.delete(`/auth/2fa/trusted-devices/${id}`);
      toast.success('Device revoked');
      fetchDevices();
    } catch { toast.error('Failed to revoke'); }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm('Revoke all trusted devices? You will need 2FA on all devices.')) return;
    try {
      await api.delete('/auth/2fa/trusted-devices');
      toast.success('All devices revoked');
      fetchDevices();
    } catch { toast.error('Failed'); }
  };

  const copyRecoveryCodes = () => {
    if (setupData?.recovery_codes) {
      navigator.clipboard.writeText(setupData.recovery_codes.join('\n'));
      setCodeCopied(true);
      toast.success('Recovery codes copied!');
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  if (loading) return <div className="p-4"><div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="2fa-settings">
      {/* Status Card */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {status?.enabled ? (
              <div className="w-10 h-10 bg-[#E6F4EA] rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#1E8E3E]" />
              </div>
            ) : (
              <div className="w-10 h-10 bg-[#FEF7E0] rounded-lg flex items-center justify-center">
                <ShieldX className="w-5 h-5 text-[#E37400]" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-[#202124]">Two-Factor Authentication</h3>
              <p className="text-sm text-[#5F6368]">
                {status?.enabled ? 'Enabled — Your account is protected with an authenticator app' : 'Not enabled — Add an extra layer of security'}
              </p>
            </div>
          </div>
          {status?.enabled ? (
            <div className="flex gap-2">
              <button onClick={fetchDevices} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1" data-testid="view-devices-btn">
                <Monitor className="w-4 h-4" /> Devices ({status.trusted_devices_count})
              </button>
              <button onClick={() => setShowDisable(true)} className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50" data-testid="disable-2fa-btn">
                Disable
              </button>
            </div>
          ) : (
            <button onClick={handleSetup} className="btn-google text-sm flex items-center gap-2" data-testid="enable-2fa-btn">
              <Shield className="w-4 h-4" /> Enable 2FA
            </button>
          )}
        </div>
      </div>

      {/* Setup Flow */}
      {setupData && !status?.enabled && (
        <div className="dashboard-card space-y-5" data-testid="2fa-setup-flow">
          <h3 className="font-semibold text-[#202124] flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#1A73E8]" /> Set Up Authenticator App
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-[#5F6368] mb-3">1. Scan this QR code with Google Authenticator or any TOTP app:</p>
              <div className="bg-white border rounded-xl p-4 inline-block">
                <img src={setupData.qr_code} alt="QR Code" className="w-48 h-48" data-testid="qr-code-img" />
              </div>
              <p className="text-xs text-[#9AA0A6] mt-2">Or enter manually: <code className="bg-[#F1F3F4] px-2 py-0.5 rounded text-xs">{setupData.secret}</code></p>
            </div>
            <div>
              <p className="text-sm text-[#5F6368] mb-3">2. Enter the 6-digit code from your app:</p>
              <div className="flex gap-2">
                <input
                  value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input-google text-center text-lg tracking-widest font-mono" placeholder="000000" maxLength={6}
                  data-testid="verify-setup-code"
                />
                <button onClick={handleVerifySetup} disabled={saving || verifyCode.length !== 6} className="btn-google" data-testid="verify-setup-btn">
                  {saving ? 'Verifying...' : 'Verify'}
                </button>
              </div>
              <div className="mt-5 p-4 bg-[#FEF7E0] border border-[#F9AB00] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-[#202124]">Recovery Codes</p>
                  <button onClick={copyRecoveryCodes} className="text-xs text-[#1A73E8] flex items-center gap-1" data-testid="copy-recovery-btn">
                    {codeCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {codeCopied ? 'Copied' : 'Copy all'}
                  </button>
                </div>
                <p className="text-xs text-[#5F6368] mb-2">Save these codes securely. Each can be used once if you lose access to your authenticator.</p>
                <div className="grid grid-cols-2 gap-1">
                  {setupData.recovery_codes.map((code, i) => (
                    <code key={i} className="text-xs bg-white px-2 py-1 rounded border font-mono">{code}</code>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable Modal */}
      {showDisable && (
        <div className="dashboard-card border-red-200" data-testid="disable-2fa-modal">
          <h3 className="font-semibold text-red-600 mb-3">Disable Two-Factor Authentication</h3>
          <p className="text-sm text-[#5F6368] mb-4">Enter your current authenticator code (or a recovery code) and password to disable 2FA.</p>
          <div className="space-y-3 max-w-sm">
            <input value={disableCode} onChange={e => setDisableCode(e.target.value)} className="input-google" placeholder="6-digit code or recovery code" data-testid="disable-code-input" />
            <input value={disablePassword} onChange={e => setDisablePassword(e.target.value)} type="password" className="input-google" placeholder="Your password" data-testid="disable-password-input" />
            <div className="flex gap-2">
              <button onClick={() => setShowDisable(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
              <button onClick={handleDisable} disabled={saving} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="confirm-disable-btn">
                {saving ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trusted Devices */}
      {showDevices && (
        <div className="dashboard-card" data-testid="trusted-devices-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#202124]">Trusted Devices</h3>
            {devices.length > 0 && (
              <button onClick={handleRevokeAll} className="text-xs text-red-600 hover:underline flex items-center gap-1" data-testid="revoke-all-btn">
                <Trash2 className="w-3 h-3" /> Revoke all
              </button>
            )}
          </div>
          {devices.length === 0 ? (
            <p className="text-sm text-[#9AA0A6]">No trusted devices</p>
          ) : (
            <div className="space-y-2">
              {devices.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-lg">
                  <div className="flex items-center gap-3">
                    <Monitor className="w-4 h-4 text-[#5F6368]" />
                    <div>
                      <p className="text-sm font-medium text-[#202124]">{d.device_name}</p>
                      <p className="text-xs text-[#9AA0A6]">Expires: {new Date(d.expires_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <button onClick={() => handleRevokeDevice(d.id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`revoke-device-${d.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
